import { useEffect, useState } from 'react';
import { Button, Card } from '@atoms/index';
import { complianceService, type LegalStatusResponse } from '@services/complianceService';

interface LegalConsentModalProps {
  legalStatus: LegalStatusResponse;
  onAccepted: () => void;
}

export function LegalConsentModal({ legalStatus, onAccepted }: LegalConsentModalProps) {
  const [accepted, setAccepted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const handleSubmit = async () => {
    if (!accepted || !legalStatus.document) return;
    setIsSubmitting(true);
    setError(null);
    try {
      await complianceService.acceptLegalDocument(legalStatus.document.id);
      onAccepted();
    } catch {
      setError('Onay kaydedilemedi. Lütfen tekrar deneyin.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center px-4 py-6">
      <div className="absolute inset-0 bg-black/70" />
      <Card className="relative z-[10001] w-full max-w-4xl max-h-[92vh] overflow-hidden flex flex-col shadow-2xl">
        <div className="p-6 border-b border-gray-100 bg-gradient-to-r from-primary/5 to-transparent">
          <h2 className="text-2xl font-bold text-gray-900">Yasal Onay Gerekli</h2>
          <p className="text-sm text-gray-600 mt-2">
            {legalStatus.document?.title ?? 'Mesafeli Satış Sözleşmesi & KVKK'} (v
            {legalStatus.document?.version}) dokümanını okuyup onaylamadan restoran panelini
            kullanamazsınız.
          </p>
        </div>

        <div className="flex-1 min-h-[320px] bg-gray-100">
          {legalStatus.document?.viewUrl && (
            <iframe
              title="Yasal doküman"
              src={legalStatus.document.viewUrl}
              className="w-full h-[50vh] min-h-[320px] border-0"
            />
          )}
        </div>

        <div className="p-6 border-t border-gray-100 space-y-4">
          {error && (
            <div className="rounded-xl bg-red-50 text-red-700 text-sm px-4 py-3">{error}</div>
          )}

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
              className="mt-1 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
            />
            <span className="text-sm text-gray-700">
              Mesafeli Satış Sözleşmesi & KVKK metnini okudum, anladım ve kabul ediyorum.
            </span>
          </label>

          <Button
            fullWidth
            size="lg"
            disabled={!accepted}
            isLoading={isSubmitting}
            onClick={handleSubmit}
          >
            Onayla ve Devam Et
          </Button>
        </div>
      </Card>
    </div>
  );
}
